import { insuranceManualVerification, InsuranceManualVerificationInput } from '@uplift/api';
import { InsuranceDataType } from '../Settings/types';
import * as Yup from 'yup';
import { useQueryClient } from '@tanstack/react-query';
import { useEditableSectionsContext } from '../../../EditableSection';

export enum NetworkStatus {
  IN_NETWORK = 'inNetwork',
  OUT_OF_NETWORK = 'outOfNetwork',
}

export const getNetworkStatusLabel = (status: NetworkStatus) => {
  switch (status) {
    case NetworkStatus.IN_NETWORK:
      return 'In-Network';
    case NetworkStatus.OUT_OF_NETWORK:
      return 'Out of Network';
    default:
      return '';
  }
};



export type InsuranceBenefitsFormType = {
  networkStatus: string | null;
  copay?: number | null;
  coinsurance?: number | null;
  remainingDeductible?: number | null;
};

export const getManualVerificationInputFromBenefitsFormValues = (values: InsuranceBenefitsFormType): Omit<InsuranceManualVerificationInput, 'insuranceId'> => {
  const { networkStatus, copay, coinsurance, remainingDeductible } = values;

  return {
    isInNetwork: networkStatus === NetworkStatus.IN_NETWORK,
    remainingDeductibleCents: (remainingDeductible || 0) * 100,
    copayCents: (copay || 0) * 100 || undefined,
    coinsurancePercentage: coinsurance || undefined,
    patientResponsibilityCents: 0
  };
};

export const useBenefitsForm = (insuranceData: InsuranceDataType) => {
  const { setEditKey } = useEditableSectionsContext();
  const queryClient = useQueryClient();

  const insuranceBenefitsFormik = () => {
    const validationSchema: Yup.ObjectSchema<InsuranceBenefitsFormType> = Yup.object({
      networkStatus: Yup.string().required('Network status is required'),
      copay: Yup.number().nullable(),
      coinsurance: Yup.number().nullable(),
      remainingDeductible: Yup.number().nullable().required('Remaining deductible is required'),
    }).test(
      'copay-or-coinsurance',
      'Either copay or coinsurance is required, but not both',
      function (value) {
        const { copay, coinsurance } = value || {};
        const hasCopay = copay != null;
        const hasCoinsurance = coinsurance != null;

        if ((hasCopay && hasCoinsurance) || (!hasCopay && !hasCoinsurance)) {
          return this.createError({ path: 'copay' }); // attach error to 'copay' field
        }

        return true;
      }
    );

    const onSubmit = async (values: InsuranceBenefitsFormType) => {
      const input = getManualVerificationInputFromBenefitsFormValues(values);

      await insuranceManualVerification({
        insuranceId: insuranceData.id,
        ...input,
      });

      await queryClient.invalidateQueries([ 'insurances' ]);
      setEditKey(null);
    };

    const initialValues: InsuranceBenefitsFormType = {
      networkStatus: insuranceData?.isInNetwork ? NetworkStatus.IN_NETWORK : NetworkStatus.OUT_OF_NETWORK,
      copay: insuranceData?.copay ?? null,
      coinsurance: insuranceData?.coinsurance || null,
      remainingDeductible: insuranceData?.remainingDeductible ?? null,
    };

    return { initialValues, onSubmit, validationSchema };
  };

  return { insuranceBenefitsFormik };
};
